import {
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { t } from "@/lib/i18n/interpolate";
import {
  type BalanceReport,
  heatForAmount,
  KJ_PER_KCAL,
  type Line,
  type ReactionEnthalpy,
  type Term,
  termMolarMass,
} from "@/lib/tool-kit/chem-reference/reaction-enthalpy";

export interface EnthalpyResultStrings {
  deltaH: string;
  heat: string;
  heatNames: { exothermic: string; endothermic: string; none: string };
  unitNames: { kJ: string; kcal: string };
  reactantSum: string;
  productSum: string;
  columnSide: string;
  columnCoefficient: string;
  columnSpecies: string;
  columnDhf: string;
  columnContribution: string;
  sideReactant: string;
  sideProduct: string;
  customMark: string;
  customNote: string;
  working: string;
  workProducts: string;
  workReactants: string;
  workDelta: string;
  unbalanced: string;
  perLabel: string;
  perReaction: string;
  perSpecies: string;
  amountLabel: string;
  amountUnitLabel: string;
  amountUnits: { mol: string; g: string };
  heatReleased: string;
  heatAbsorbed: string;
  copy: string;
  copied: string;
  copyText: string;
}

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const NUMBER = { maximumFractionDigits: 2, useGrouping: false } as const;

export type EnergyUnit = "kJ" | "kcal";

function heatKind(delta: number): "exothermic" | "endothermic" | "none" {
  if (delta < 0) {
    return "exothermic";
  }
  return delta > 0 ? "endothermic" : "none";
}

export const inUnit = (kj: number, unit: EnergyUnit) =>
  unit === "kcal" ? kj / KJ_PER_KCAL : kj;

function termLabel(term: Term): string {
  return term.state ? `${term.formula}(${term.state})` : term.formula;
}

function SpeciesTable({
  result,
  strings,
  fmt,
}: {
  result: ReactionEnthalpy;
  strings: EnthalpyResultStrings;
  fmt: Fmt;
}) {
  const row = (line: Line, side: string) => [
    side,
    fmt(line.term.coefficient, NUMBER),
    termLabel(line.term),
    `${fmt(line.term.entry?.dHf ?? 0, NUMBER)}${line.term.custom ? ` ${strings.customMark}` : ""}`,
    fmt(line.contribution, NUMBER),
  ];
  return (
    <DataTable
      head={[
        strings.columnSide,
        strings.columnCoefficient,
        strings.columnSpecies,
        strings.columnDhf,
        strings.columnContribution,
      ]}
      rows={[
        ...result.reactants.map((line) => row(line, strings.sideReactant)),
        ...result.products.map((line) => row(line, strings.sideProduct)),
      ]}
    />
  );
}

function sumText(lines: readonly Line[], fmt: Fmt): string {
  return (
    lines
      .map(
        (line) =>
          `${fmt(line.term.coefficient, NUMBER)} × (${fmt(line.term.entry?.dHf ?? 0, NUMBER)})`
      )
      .join(" + ") || "0"
  );
}

export function PerSpeciesControls({
  terms,
  selected,
  onSelect,
  amount,
  onAmount,
  amountUnit,
  onAmountUnit,
  strings,
}: {
  terms: readonly Term[];
  selected: string;
  onSelect: (value: string) => void;
  amount: string;
  onAmount: (value: string) => void;
  amountUnit: "mol" | "g";
  onAmountUnit: (value: "mol" | "g") => void;
  strings: EnthalpyResultStrings;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <SelectField
        id="er-per"
        label={strings.perLabel}
        onChange={onSelect}
        options={[
          { value: "reaction", label: strings.perReaction },
          ...terms.map((term, index) => ({
            value: String(index),
            label: t(strings.perSpecies, { species: termLabel(term) }),
          })),
        ]}
        value={selected}
      />
      {selected === "reaction" ? null : (
        <div className="grid grid-cols-[2fr_1fr] gap-2">
          <TextField
            id="er-amount"
            label={strings.amountLabel}
            numeric
            onChange={onAmount}
            value={amount}
          />
          <SelectField
            id="er-amount-unit"
            label={strings.amountUnitLabel}
            onChange={onAmountUnit}
            options={[
              { value: "mol", label: strings.amountUnits.mol },
              { value: "g", label: strings.amountUnits.g },
            ]}
            value={amountUnit}
          />
        </div>
      )}
    </div>
  );
}

export function EnthalpyResults({
  result,
  balance,
  unit,
  heatTerm,
  heatMoles,
  strings,
  fmt,
}: {
  result: ReactionEnthalpy;
  balance: BalanceReport;
  unit: EnergyUnit;
  /** The species the heat is expressed for, and the moles of it; null for "as written". */
  heatTerm: Term | null;
  heatMoles: number | null;
  strings: EnthalpyResultStrings;
  fmt: Fmt;
}) {
  const unitName = strings.unitNames[unit];
  const show = (kj: number) => `${fmt(inUnit(kj, unit), NUMBER)}`;
  const heatKey = heatKind(result.delta);
  const delta = `${show(result.delta)} ${unitName}`;
  const total =
    heatTerm && heatMoles !== null
      ? heatForAmount(result.delta, heatTerm.coefficient, heatMoles)
      : null;
  const custom = [...result.reactants, ...result.products].some(
    (line) => line.term.custom
  );
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.deltaH} value={delta} />
        <StatCard label={strings.heat} value={strings.heatNames[heatKey]} />
        <StatCard
          label={strings.productSum}
          value={`${show(result.productSum)} ${unitName}`}
        />
        <StatCard
          label={strings.reactantSum}
          value={`${show(result.reactantSum)} ${unitName}`}
        />
        {total === null ? null : (
          <StatCard
            label={t(total < 0 ? strings.heatReleased : strings.heatAbsorbed, {
              species: heatTerm ? termLabel(heatTerm) : "",
            })}
            value={`${show(Math.abs(total))} ${unitName}`}
          />
        )}
      </StatGrid>
      {balance.balanced ? null : (
        <Notice>
          {t(strings.unbalanced, { items: balance.differing.join(", ") })}
        </Notice>
      )}
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.columnSpecies}</h3>
        <SpeciesTable fmt={fmt} result={result} strings={strings} />
      </div>
      {custom ? <Notice>{strings.customNote}</Notice> : null}
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>
          {t(strings.workProducts, {
            sum: sumText(result.products, fmt),
            total: fmt(result.productSum, NUMBER),
          })}
        </p>
        <p>
          {t(strings.workReactants, {
            sum: sumText(result.reactants, fmt),
            total: fmt(result.reactantSum, NUMBER),
          })}
        </p>
        <p>
          {t(strings.workDelta, {
            products: fmt(result.productSum, NUMBER),
            reactants: fmt(result.reactantSum, NUMBER),
            delta,
          })}
        </p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, { delta, heat: strings.heatNames[heatKey] })}
      />
    </>
  );
}

/** Moles of a species from an amount typed in mol or g. */
export function molesFrom(
  term: Term,
  amount: number,
  unit: "mol" | "g"
): number {
  return unit === "mol" ? amount : amount / termMolarMass(term);
}
